import { useEffect, useMemo, useState } from "react";
import { usePermissions } from "../rbac/PermissionsContext";
import { employeeService } from "../services/api";

/**
 * Whether "My Team" should appear in shell / ESS navigation.
 *
 * Shown only when the signed-in user has at least one direct reportee.
 * Managers (or admins) with My Team RBAC but an empty team do not see the tab.
 */
export function useMyTeamNavVisibility() {
  const { loading, snapshot } = usePermissions();
  const [directReporteeCount, setDirectReporteeCount] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (loading && !snapshot) {
      setDirectReporteeCount(null);
      return undefined;
    }

    setDirectReporteeCount(null);
    (async () => {
      try {
        const resp = await employeeService.getMyReportees();
        if (cancelled) return;
        if (resp?.success && Array.isArray(resp.data)) {
          setDirectReporteeCount(resp.data.length);
        } else {
          setDirectReporteeCount(0);
        }
      } catch {
        if (!cancelled) setDirectReporteeCount(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, snapshot]);

  return useMemo(() => {
    if (loading && !snapshot) return false;
    if (directReporteeCount == null) return false;
    return directReporteeCount > 0;
  }, [loading, snapshot, directReporteeCount]);
}
